import axios from "axios";
import Links from "../constants/Links";


/**
 * This is the Student Information API hook for handling Student Information in the system (C) 2021-2023
 * 
 * */


const StudentInformation = {
    /**
     * fetch student information
     * @param {string} id student userId
     * @returns {Promise<object>}
     */
    getInformation: async (id) => {
        return axios.get(Links.studentInformation.getInformation + '?id=' + id)
    },

    /**
     * update student information
     * @param {string} id student id
     * @param {object} data info to be updated
     * @returns 
     */
    updateInformation: async (id, data) => {
        return axios.put(Links.studentInformation.updateInformation + '?id=' + id, data)
    },

    /**
     * update student supervisor
     * @param {string} studentId student userId
     * @param {number} supervisorId lecturer id
     * @returns {Promise<object>}
     */
    updateSupervisor: async (studentId, supervisorId) => {
        return axios.put(Links.studentInformation.updateSupervisor + '?studentId=' + studentId + '&supervisorId=' + supervisorId)
    },

    /**
     * 
     * @param {string} id 
     * @returns 
     */
    getIDCardDetails: async (id) => {
        return axios.get(Links.studentInformation.getIDCardDetails + '?id=' + id)
    },

    /**
     * download student information (.pdf)
     * @param {string} studentId user id of the student
     *@returns {Promise<Blob>} 
     */
    download: async (studentId) => {
        return axios.get(Links.studentInformation.download + '?student=' + studentId, {
            responseType: 'blob'
        })
    },

    /**
     * 
     * @param {*} faculty 
     * @param {*} session 
     * @returns 
     */
    downloadMigrationReport: async (faculty, session) => {
        return axios.get(Links.studentInformation.downloadMigrationReport + '?faculty=' + faculty + '&session=' + session, {
            responseType: 'blob'
        })
    },

    /**
     * @description Downloads ID cards fro students (.pdf)
     * @param reqParams an object containing session and optional faculty, department, programme, level, paymentDate and barcode (true for a matric-number barcode)
     * @returns {Promise<any[]>} a pdf document
     */
    cards: async (reqParams) => {
        let params = '?session=' + reqParams.session

        if(reqParams.faculty){
            params = params + '&faculty=' + reqParams.faculty
        }

        if(reqParams.department){
            params = params + '&department=' + reqParams.department
        }

        if(reqParams.programme){
            params = params + '&programme=' + reqParams.programme
        }

        if(reqParams.level){
            params = params + '&level=' + reqParams.level
        }

        if(reqParams.paymentDate){
            params = params + '&date=' + reqParams.paymentDate
        }
        if (reqParams.barcode === true) {
            params = params + '&barcode=true'
        }
        return axios.get(Links.studentInformation.cards + params, {
            responseType: 'blob'
        })
    },

    /**
     * @description Downloads ID for a single student(.pdf)
     * @param {string} studentId the ID of the student to download ID card for
     * @param {boolean} barcode print a matric-number barcode when true; defaults to existing QR behavior
     * @returns {Promise<Blob>} a pdf document
     */
    cardSingle: async (studentId, barcode = false) => {
        const params = '?student=' + studentId + (barcode === true ? '&barcode=true' : '')
        return axios.get(Links.studentInformation.cardSingle + params, {
            responseType: 'blob'
        })
    },

    updateNin: async (data) => {
        return axios.put(Links.studentInformation.updateNin, data);
    },

    /**
     * List the sessions a student may fetch results for without a payment/progress
     * record (transfer-student overrides).
     * @param {string} id student userId / matric number
     * @returns {Promise<object>} 200 -> array of { id, name }
     */
    listResultSessionOverrides: async (id) => {
        return axios.get(Links.studentInformation.resultSessionOverrides + '?id=' + encodeURIComponent(id))
    },

    /**
     * Grant a result-session override (idempotent).
     * @param {string} id student userId / matric number
     * @param {number} sessionId numeric Session primary key
     * @returns {Promise<object>} 200 -> updated StudentInformation
     */
    grantResultSessionOverride: async (id, sessionId) => {
        return axios.post(Links.studentInformation.resultSessionOverride + '?id=' + encodeURIComponent(id) + '&sessionId=' + sessionId)
    },

    /**
     * Revoke a result-session override (no error if it wasn't set).
     * @param {string} id student userId / matric number
     * @param {number} sessionId numeric Session primary key
     * @returns {Promise<object>} 200 -> updated StudentInformation
     */
    revokeResultSessionOverride: async (id, sessionId) => {
        return axios.delete(Links.studentInformation.resultSessionOverride + '?id=' + encodeURIComponent(id) + '&sessionId=' + sessionId)
    },
}

export default StudentInformation
